(() => {
  const elementName = 'libas-product-gallery-countdown';
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  if (
    !('customElements' in window) ||
    typeof Intl === 'undefined' ||
    typeof Intl.DateTimeFormat !== 'function' ||
    customElements.get(elementName)
  ) return;

  let formatter;
  try {
    formatter = new Intl.DateTimeFormat('en-GB-u-hc-h23', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
  } catch (error) {
    return;
  }

  const parts = (date) => {
    const values = {};
    formatter.formatToParts(date).forEach((part) => {
      if (part.type !== 'literal') values[part.type] = part.value;
    });
    const weekday = weekdays[values.weekday];
    const year = Number(values.year);
    const month = Number(values.month);
    const day = Number(values.day);
    const minute = Number(values.hour) * 60 + Number(values.minute);
    return Number.isInteger(weekday) && Number.isInteger(year) && Number.isInteger(month) &&
      Number.isInteger(day) && Number.isInteger(minute)
      ? { year, month, day, weekday, minute }
      : null;
  };

  const parseTime = (value) => {
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value || '')) return null;
    const [hour, minute] = value.split(':').map(Number);
    return hour * 60 + minute;
  };

  const dateAt = (current, offset) => {
    const date = new Date(Date.UTC(current.year, current.month - 1, current.day + offset));
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
  };

  const toTimestamp = (date, minute) =>
    Date.UTC(date.year, date.month - 1, date.day, Math.floor(minute / 60), minute % 60) - 330 * 60 * 1000;

  const parseOnce = (dateValue, timeValue) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue || '');
    const minute = parseTime(timeValue);
    if (!match || minute === null) return null;
    const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
    if (date.year < 2000 || date.year > 2999) return null;
    const timestamp = toTimestamp(date, minute);
    const verified = parts(new Date(timestamp));
    return verified && verified.year === date.year && verified.month === date.month &&
      verified.day === date.day && verified.minute === minute ? timestamp : null;
  };

  const windowFor = (element, current, dayName, date, minute) => {
    if (element.dataset[`${dayName}Enabled`] !== 'true') return null;
    const start = parseTime(element.dataset[`${dayName}StartTime`]);
    const end = parseTime(element.dataset[`${dayName}EndTime`]);
    if (start === null || end === null || start === end) return null;
    if (start < end && minute >= start && minute < end) {
      return { start: toTimestamp(date, start), end: toTimestamp(date, end) };
    }
    if (start > end && minute >= start) {
      return { start: toTimestamp(date, start), end: toTimestamp(dateAt(date, 1), end) };
    }
    return null;
  };

  const activeWindow = (element, now) => {
    const mode = element.dataset.scheduleMode;
    if (mode === 'once') {
      const start = parseOnce(element.dataset.onceStartDate, element.dataset.onceStartTime);
      const end = parseOnce(element.dataset.onceEndDate, element.dataset.onceEndTime);
      return start !== null && end !== null && now >= start && now < end ? { start, end } : null;
    }

    const current = parts(new Date(now));
    if (!current) return null;
    const date = { year: current.year, month: current.month, day: current.day };

    if (mode === 'daily') {
      const start = parseTime(element.dataset.dailyStartTime);
      const end = parseTime(element.dataset.dailyEndTime);
      if (start === null || end === null || start === end) return null;
      if (start < end && current.minute >= start && current.minute < end) {
        return { start: toTimestamp(date, start), end: toTimestamp(date, end) };
      }
      if (start > end && current.minute >= start) {
        return { start: toTimestamp(date, start), end: toTimestamp(dateAt(date, 1), end) };
      }
      if (start > end && current.minute < end) {
        return { start: toTimestamp(dateAt(date, -1), start), end: toTimestamp(date, end) };
      }
      return null;
    }

    if (mode !== 'weekly') return null;
    const currentWindow = windowFor(element, current, days[current.weekday], date, current.minute);
    if (currentWindow) return currentWindow;

    const previousWeekday = (current.weekday + 6) % 7;
    const previousName = days[previousWeekday];
    const start = parseTime(element.dataset[`${previousName}StartTime`]);
    const end = parseTime(element.dataset[`${previousName}EndTime`]);
    if (
      element.dataset[`${previousName}Enabled`] === 'true' &&
      start !== null && end !== null && start > end && current.minute < end
    ) {
      return { start: toTimestamp(dateAt(date, -1), start), end: toTimestamp(date, end) };
    }
    return null;
  };

  class ProductGalleryCountdown extends HTMLElement {
    connectedCallback() {
      this.stop();
      this.hours = this.querySelector('[data-gallery-timer-hours]');
      this.minutes = this.querySelector('[data-gallery-timer-minutes]');
      this.seconds = this.querySelector('[data-gallery-timer-seconds]');
      if (!this.hours || !this.minutes || !this.seconds) return this.expire();
      this.onVisibilityChange = () => { if (!document.hidden) this.tick(); };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.tick();
    }

    disconnectedCallback() { this.stop(); }

    tick() {
      window.clearTimeout(this.timeout);
      const now = Date.now();
      const scheduleWindow = activeWindow(this, now);
      if (!scheduleWindow || scheduleWindow.end <= now) {
        this.hidden = true;
      } else {
        const remaining = Math.ceil((scheduleWindow.end - now) / 1000);
        this.hours.textContent = String(Math.floor(remaining / 3600)).padStart(2, '0');
        this.minutes.textContent = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
        this.seconds.textContent = String(remaining % 60).padStart(2, '0');
        this.hidden = false;
      }
      this.timeout = window.setTimeout(() => this.tick(), 1000 - (Date.now() % 1000) + 20);
    }

    expire() { this.hidden = true; this.stop(); }
    stop() {
      window.clearTimeout(this.timeout);
      this.timeout = null;
      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.onVisibilityChange = null;
      }
    }
  }

  customElements.define(elementName, ProductGalleryCountdown);
})();
